/**
 * Cell renderer genérico para el merge silencioso entre toma y catalog.
 *
 * Diseño: el merge es una PROPIEDAD de una columna existente (Core o Native).
 * En la config:
 *   columnConfig.core.Marca = { enabled: true, mergeWithCatalog: 'catalog_brand' }
 *   columnConfig.native.foo = { enabled: true, mergeWithCatalog: 'catalog_category' }
 *
 * Este componente recibe la `row` completa, el `tomaKey` (campo a leer de la
 * toma para esta columna) y el `catalogField` (campo del JOIN catalog a comparar).
 *
 * Política de prioridad (modo `catalogAuthoritative=true`, default cuando la
 * columna declara `mergeWithCatalog`):
 *   1. `manually_edited_fields` incluye `catalogField` → catalog wins
 *      → tooltip "Origen: Cat (Manual)"
 *   2. catalogField tiene valor → catalog wins → tooltip "Origen: Cat"
 *   3. catalogField null/vacío → "—" con tooltip "Origen: Catalog (vacío)"
 *      (NO fallback a toma — la cell respeta el bucket del filter)
 *
 * Modo legacy (`catalogAuthoritative=false`): si catalog está vacío hace
 * fallback a tomaVal con tooltip "Origen: Toma". Se preserva para clientes
 * pre-Gold sin merge activo o llamadas que no propaguen el flag.
 *
 * Tooltip: HTML title nativo (ligero, sin librería).
 */
import React from 'react';
import { MERGE_CATALOG_FIELDS } from '../../shared/mergeCatalogFields';
import type { CatalogField } from '../../shared/mergeCatalogFields';

// PT-#83: la whitelist de merge fields vive en la single source of truth
// src/shared/mergeCatalogFields.ts (paridad con backend server/utils/mergeCatalogFields.js).
// Se re-exportan los nombres históricos para no romper imports existentes
// (ColumnConfigSection consume VALID_CATALOG_FIELDS desde acá).
export type { CatalogField };

export const VALID_CATALOG_FIELDS: CatalogField[] = [...MERGE_CATALOG_FIELDS];

interface MergedTomaCatalogCellProps {
  row: any;
  tomaKey: string;        // columnKey de la columna que se renderiza (ej: 'Marca', 'Cat', sourceField de native)
  catalogField: string;   // valor del flag mergeWithCatalog (whitelist)
  /**
   * Cuando true, catalog es la SINGLE SOURCE OF TRUTH. Si `catalogVal` es null/vacío
   * la cell muestra `—` con tooltip "Origen: Catalog (vacío)" y NO hace fallback a
   * tomaVal. Esto preserva consistencia entre filter (bucket "(vacío)"), display y
   * export para columnas declaradas con `mergeWithCatalog`.
   * Default: true (el componente solo se monta cuando la columna tiene merge activo).
   * Para conservar el comportamiento legacy (fallback a toma), pasar `false`.
   */
  catalogAuthoritative?: boolean;
}

function isNonEmpty(v: unknown): boolean {
  if (v === null || v === undefined) return false;
  if (typeof v === 'string') {
    const t = v.trim();
    return t !== '' && t.toLowerCase() !== 'null' && t !== '(NULL)';
  }
  return true;
}

/**
 * Strip trailing zeros para valores numéricos del catalog stored como DECIMAL(15,3).
 * MariaDB devuelve "15.000", "3.500", "750.000" — el usuario quiere "15", "3.5", "750".
 * Solo aplica al field `contenido` (los otros son texto).
 * Conserva el valor crudo si no parsea como número (ej. "1.5L" o vacío).
 */
export function formatContenidoValue(raw: unknown): string {
  if (raw === null || raw === undefined) return '';
  const s = String(raw).trim();
  if (s === '') return '';
  // Solo strip si parsea como número limpio
  const n = Number(s);
  if (Number.isFinite(n)) return n.toString();
  return s;
}

function parseManuallyEdited(raw: unknown): string[] {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed.map(String) : [];
    } catch {
      return [];
    }
  }
  return [];
}

export const MergedTomaCatalogCell: React.FC<MergedTomaCatalogCellProps> = ({
  row,
  tomaKey,
  catalogField,
  catalogAuthoritative = true,
}) => {
  const tomaVal = row?.[tomaKey];
  const catalogVal = row?.[catalogField];
  const editedFields = parseManuallyEdited(row?.manually_edited_fields);
  const wasManual = editedFields.includes(catalogField);
  // Bug 2: catalog.contenido es DECIMAL(15,3) → "15.000". Strip trailing zeros para display.
  const formatValue = catalogField === 'contenido' ? formatContenidoValue : (v: unknown) => String(v);

  // Catalog wins si tiene valor (manual o IA) — válido en ambos modos.
  if (isNonEmpty(catalogVal)) {
    const tooltip = wasManual ? 'Origen: Cat (Manual)' : 'Origen: Cat';
    return <span title={tooltip}>{formatValue(catalogVal)}</span>;
  }

  // catalogVal vacío.
  if (catalogAuthoritative) {
    // Single source of truth = catalog. Si está vacío, mostrar — sin fallback toma.
    // Mantiene consistencia con filter dropdown (bucket "(vacío)") y export XLSX.
    return (
      <span className="text-gray-400" title="Origen: Catalog (vacío)">
        —
      </span>
    );
  }

  // Modo legacy: ambos null → guion sin tooltip.
  if (!isNonEmpty(tomaVal)) {
    return <span className="text-gray-400">—</span>;
  }

  // Modo legacy: toma wins como fallback (preserva compat pre-merge).
  return <span title="Origen: Toma">{formatValue(tomaVal)}</span>;
};

export default MergedTomaCatalogCell;
